import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase";

const lodgeNumber = value => String(value ?? "").trim();
export function canViewDiningBooking(user, number) {
  if (!user || user.active === false || !lodgeNumber(number)) return false;
  if (["admin", "superuser"].includes(user.role)) return true;
  if (user.role !== "treasurer") return false;
  const access = Array.isArray(user.lodgeAccess) && user.lodgeAccess.length ? user.lodgeAccess.map(lodge => lodge?.number) : [user.lodgeNumber];
  return access.some(value => lodgeNumber(value) === lodgeNumber(number));
}

export function matchingDiningBookings(records, event) {
  if (!lodgeNumber(event?.lodgeNumber) || !event?.date) return [];
  return records.filter(booking => booking.date === event.date &&
    lodgeNumber(booking.lodgeNumber) === lodgeNumber(event.lodgeNumber) && booking.status !== "cancelled")
    .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")) || a.id.localeCompare(b.id));
}

export function useDiningBookingMatches(event, user) {
  const number = lodgeNumber(event?.lodgeNumber), date = event?.date;
  const allowed = canViewDiningBooking(user, number);
  const [retry, setRetry] = useState(0), [state, setState] = useState({});
  const key = JSON.stringify([user?.id, user?.role, allowed, number, date, retry]);
  useEffect(() => {
    if (!allowed || !date) return;
    // Exact lodge/date query, independent of the recent/history booking list.
    // Older numeric lodge numbers remain compatible without fuzzy name matching.
    const numbers = [number];
    if (Number.isSafeInteger(Number(number)) && String(Number(number)) === number) numbers.push(Number(number));
    const source = query(collection(db, "bookings"), where("date", "==", date), where("lodgeNumber", "in", numbers));
    let active = true;
    const unsubscribe = onSnapshot(source, snapshot => {
      if (!active) return;
      const records = matchingDiningBookings(snapshot.docs.map(item => ({ ...item.data(), id: item.id })), { date, lodgeNumber: number });
      // Only the summary belongs in the calendar. Full details load on opening.
      setState({ key, records: records.map(({ id, lodgeNumber, date, time, status, diners, courseCount }) =>
        ({ id, lodgeNumber, date, time, status, diners, courseCount })) });
    }, () => { if (active) setState({ key, records: [], error: "Dining bookings could not be checked. Please try again." }); });
    return () => { active = false; unsubscribe(); };
  }, [allowed, number, date, key]);
  return { ...(state.key === key ? state : { records: [], loading: allowed && !!date }),
    allowed, retry: () => setRetry(value => value + 1) };
}

export function useLinkedDiningBooking(target, user) {
  const allowed = canViewDiningBooking(user, target?.lodgeNumber);
  const [retry, setRetry] = useState(0), [state, setState] = useState({});
  const id = target?.id, date = target?.date, number = lodgeNumber(target?.lodgeNumber);
  const key = JSON.stringify([user?.id, user?.role, allowed, id, date, number, retry]);
  useEffect(() => {
    if (!id || !allowed) return;
    let active = true;
    const unsubscribe = onSnapshot(doc(db, "bookings", id), snapshot => {
      if (!active) return;
      const booking = snapshot.exists() ? { ...snapshot.data(), id: snapshot.id } : null;
      // Recheck the match: the record may have moved or been cancelled since clicking.
      if (!booking || !matchingDiningBookings([booking], { date, lodgeNumber: number }).length) {
        setState({ key, error: "This dining booking is no longer available for that lodge and date. Return to the calendar to check the latest match." });
      } else setState({ key, booking: { ...booking, selectedCourses: booking.selectedCourses || {} } });
    }, () => { if (active) setState({ key, error: "The dining booking could not be opened. Please try again." }); });
    return () => { active = false; unsubscribe(); };
  }, [id, allowed, date, number, key]);
  return { ...(state.key === key ? state : { loading: !!id && allowed }),
    error: !allowed && id ? "You do not have dining access for this lodge." : state.key === key ? state.error : undefined,
    retry: () => setRetry(value => value + 1) };
}
